import React, { useState, useEffect } from "react";
import { Row, Col, Card, Typography, Collapse, Spin, message, Button, Radio, Space } from "antd";
import { Line, Chart } from 'react-chartjs-2';
import { Chart as ChartJS, registerables } from 'chart.js';
import axios from "axios";
import moment from 'moment'

import PageHeader from "../Layout/PageHeader";
import AnalysisFilters from './AnalysisFilters'
import { usePermissions } from "../App/PermissionContext";
import { PERMISSIONS } from "../../constants/roles";
import { DownloadOutlined } from "@ant-design/icons";
import { ACTIVE_INTERNAL_STATUSES } from "../../constants/status";
import { CALENDAR_MONTHS } from "../../constants/dates";
import { SERVICE_PROVIDER_DETAILS } from "../../constants/serviceProviders"

ChartJS.register(...registerables);

const DIMENSIONS = {
  time: [
    { label: "Week", value: "week" },
    { label: "Month", value: "month" },
  ],
};

const endOfThisMonth = moment().endOf('month')
const threeMonthsAgo = moment().subtract(3, 'months').startOf('month')

const serviceProviders = SERVICE_PROVIDER_DETAILS

const CourierAnalysis = () => {
  const [filters, setFilters] = useState({ startDate: threeMonthsAgo, endDate: endOfThisMonth })
  const [dimension, setDimension] = useState({ time: "month" })
  const [courierOrderStats, setCourierOrderStats] = useState([])
  const [weeklyCourierOrderStats, setWeeklyCourierOrderStats] = useState([])
  const [loading, setLoading] = useState({ order: false })

  const { permissions } = usePermissions();

  useEffect(() => {
    const dateParams = {
      startDate: filters.startDate?.startOf('month')?.toISOString(),
      endDate: filters.endDate?.endOf('month')?.toISOString(),
    }

    const fetchCourierOrderStats = async () => {
      setLoading(prevLoading => ({ ...prevLoading, order: true }))

      try {
        const res = await axios.get(`/api/analysis/monthly-courier-order`, {
          params: { ...dateParams },
          timeout: 1000 * 300
        })
        const rawData = res.data.data
        setCourierOrderStats(rawData)

        // Flatten weekly data from the nested response
        const weeklyData = rawData.map(courier => ({
          serviceProvider: courier.serviceProvider,
          data: courier.data.flatMap(item =>
            item.weeks.map(week => ({
              year: item.year,
              month: item.month,
              week: week.week,
              orderCount: week.orderCount,
              totalPrice: week.totalPrice,
            }))
          )
        }))
        setWeeklyCourierOrderStats(weeklyData)
      } catch (error) {
        message.error(error.response?.data?.message || error.message, 0);
      }

      setLoading(prevLoading => ({ ...prevLoading, order: false }))
    }

    fetchCourierOrderStats()
  }, [filters.startDate, filters.endDate])

  return (
    <React.Fragment>
      <PageHeader title="Courier Order Analytics" childRoutes={[]} />

      <Collapse>
        <Collapse.Panel header={`Filters (${filters.startDate.format('MMM YYYY')} - ${filters.endDate.format('MMM YYYY')})`} key="filters">
          <Space wrap>
            <AnalysisFilters
              filters={filters}
              onFiltersChange={filters => setFilters(filters)}
            />
            Time Dimension :
            <Radio.Group
              options={DIMENSIONS.time}
              value={dimension.time}
              onChange={(e) => setDimension({ ...dimension, time: e.target.value })}
              optionType="button"
              buttonStyle="solid"
            />
          </Space>
        </Collapse.Panel>
      </Collapse>

      <Row className="mt-2 mb-2" justify="end" align="end">
        {permissions?.includes(PERMISSIONS.EXPORT_ORDER_LIST) && (
          <Button
            onClick={() => {
              const params = new URLSearchParams({
                internalstatus: ACTIVE_INTERNAL_STATUSES,
                startDate: filters.startDate,
                endDate: filters.endDate
              }).toString();

              window.open(`/all-order?${params}`, "_blank");
            }}
          >
            CSV <DownloadOutlined />
          </Button>
        )}
      </Row>

      <Typography.Title level={4} className="mt-4">Orders</Typography.Title>

      {dimension.time === "month" && (
        <>
          <Row gutter={16}>
            <Col md={12}>
              <Card title="Order Count by Couriers">
              {loading.order
                ? <Spin />
                : <Line
                  data={{
                    labels: courierOrderStats[0]?.data?.map(stat => `${stat.year}-${stat.month}`),
                    datasets: courierOrderStats.map(courierOrder => {
                      return {
                        label: courierOrder.serviceProvider,
                        data: courierOrder.data.map(stat => stat.monthlyOrderCount),
                        borderColor: serviceProviders.find(sp => sp.key === courierOrder.serviceProvider)?.color,
                        backgroundColor: serviceProviders.find(sp => sp.key === courierOrder.serviceProvider)?.color
                      }
                    })
                  }}
                  options={{
                    interaction: {
                      mode: 'index'
                    }
                  }}
                />}
              </Card>
            </Col>

            <Col md={12}>
              <Card title="Total Order Price by Couriers (MYR)">
              {loading.order
                ? <Spin />
                : <Line
                  data={{
                    labels: courierOrderStats[0]?.data?.map(stat => `${stat.year}-${stat.month}`),
                    datasets: courierOrderStats.map(courierOrder => {
                      return {
                        label: courierOrder.serviceProvider,
                        data: courierOrder.data.map(stat => stat.monthlyTotalPrice),
                        borderColor: serviceProviders.find(sp => sp.key === courierOrder.serviceProvider)?.color,
                        backgroundColor: serviceProviders.find(sp => sp.key === courierOrder.serviceProvider)?.color
                      }
                    })
                  }}
                  options={{
                    interaction: {
                      mode: 'index'
                    }
                  }}
                />}
              </Card>
            </Col>
          </Row>

          <Row gutter={16} className="mt-4">
            <Col md={12}>
              <Card title="Overall Order (Total Price & Count)">
              {loading.order
                ? <Spin />
                : <Chart
                  type="line"
                  data={{
                    labels: courierOrderStats[0]?.data?.map(stat => `${stat.year}-${stat.month}`),
                    datasets: [{
                      label: 'Total Price (MYR)',
                      type: 'line',
                      backgroundColor: 'rgb(255, 99, 132)',
                      borderColor: 'rgb(255, 99, 132)',
                      yAxisID: 'A',
                      data: courierOrderStats[0]?.data?.map((_, index) => {
                        return courierOrderStats.reduce((prev, curr) => prev + curr.data[index]?.monthlyTotalPrice, 0);
                      }),
                    },
                    {
                      label: 'Count',
                      type: 'bar',
                      backgroundColor: 'rgb(132, 99, 255)',
                      borderColor: 'rgb(132, 99, 255)',
                      yAxisID: 'B',
                      data: courierOrderStats[0]?.data?.map((_, index) => {
                        return courierOrderStats.reduce((prev, curr) => prev + curr.data[index]?.monthlyOrderCount, 0);
                      }),
                    }]
                  }}
                  options={{
                    scales: {
                      A: {
                          type: 'linear',
                          position: 'left',
                          ticks: { color: 'rgb(255, 99, 132)' },
                      },
                      B: {
                          type: 'linear',
                          position: 'right',
                          ticks: { color: 'rgb(132, 99, 255)' },
                      }
                    }
                  }}
                />}
              </Card>
            </Col>
          </Row>
        </>
      )}

      {dimension.time === "week" && (
        <>
          <Row gutter={16}>
            <Col md={12}>
              <Card title="Order Count by Couriers">
              {loading.order
                ? <Spin />
                : <Line
                  data={{
                    labels: weeklyCourierOrderStats[0]?.data?.map(stat => {
                      const monthName = CALENDAR_MONTHS[stat.month - 1]?.name;
                      return stat.week === 1
                        ? [`W${stat.week}`, monthName?.substring(0, 3), stat.year]
                        : [`W${stat.week}`];
                    }),
                    datasets: weeklyCourierOrderStats.map(weeklyCourierOrder => {
                      return {
                        label: weeklyCourierOrder.serviceProvider,
                        data: weeklyCourierOrder.data.map(stat => stat.orderCount),
                        borderColor: serviceProviders.find(sp => sp.key === weeklyCourierOrder.serviceProvider)?.color,
                        backgroundColor: serviceProviders.find(sp => sp.key === weeklyCourierOrder.serviceProvider)?.color
                      }
                    })
                  }}
                  options={{
                    interaction: {
                      mode: 'index'
                    }
                  }}
                />}
              </Card>
            </Col>

            <Col md={12}>
              <Card title="Total Order Price by Couriers (MYR)">
              {loading.order
                ? <Spin />
                : <Line
                  data={{
                    labels: weeklyCourierOrderStats[0]?.data?.map(stat => {
                      const monthName = CALENDAR_MONTHS[stat.month - 1]?.name;
                      return stat.week === 1
                        ? [`W${stat.week}`, monthName?.substring(0, 3), stat.year]
                        : [`W${stat.week}`];
                    }),
                    datasets: weeklyCourierOrderStats.map(weeklyCourierOrder => {
                      return {
                        label: weeklyCourierOrder.serviceProvider,
                        data: weeklyCourierOrder.data.map(stat => stat.totalPrice),
                        borderColor: serviceProviders.find(sp => sp.key === weeklyCourierOrder.serviceProvider)?.color,
                        backgroundColor: serviceProviders.find(sp => sp.key === weeklyCourierOrder.serviceProvider)?.color
                      }
                    })
                  }}
                  options={{
                    interaction: {
                      mode: 'index'
                    }
                  }}
                />}
              </Card>
            </Col>
          </Row>

          <Row gutter={16} className="mt-4">
            <Col md={12}>
              <Card title="Overall Order (Total Price & Count)">
              {loading.order
                ? <Spin />
                : <Chart
                  type="line"
                  data={{
                    labels: weeklyCourierOrderStats[0]?.data?.map(stat => {
                      const monthName = CALENDAR_MONTHS[stat.month - 1]?.name;
                      return stat.week === 1
                        ? [`W${stat.week}`, monthName?.substring(0, 3), stat.year]
                        : [`W${stat.week}`];
                    }),
                    datasets: [{
                      label: 'Total Price (MYR)',
                      type: 'line',
                      backgroundColor: 'rgb(255, 99, 132)',
                      borderColor: 'rgb(255, 99, 132)',
                      yAxisID: 'A',
                      data: weeklyCourierOrderStats[0]?.data?.map((_, index) => {
                        return weeklyCourierOrderStats.reduce((prev, curr) => prev + curr.data[index]?.totalPrice, 0);
                      }),
                    },
                    {
                      label: 'Count',
                      type: 'bar',
                      backgroundColor: 'rgb(132, 99, 255)',
                      borderColor: 'rgb(132, 99, 255)',
                      yAxisID: 'B',
                      data: weeklyCourierOrderStats[0]?.data?.map((_, index) => {
                        return weeklyCourierOrderStats.reduce((prev, curr) => prev + curr.data[index]?.orderCount, 0);
                      }),
                    }]
                  }}
                  options={{
                    scales: {
                      A: {
                          type: 'linear',
                          position: 'left',
                          ticks: { color: 'rgb(255, 99, 132)' },
                      },
                      B: {
                          type: 'linear',
                          position: 'right',
                          ticks: { color: 'rgb(132, 99, 255)' },
                      }
                    }
                  }}
                />}
              </Card>
            </Col>
          </Row>
        </>
      )}
    </React.Fragment>
  )
}

export default CourierAnalysis;
